import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import {
  Checkbox,
  Button,
  Box,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageTitle from "../common/PageTitle";
import BillLogo from "./shared/BillLogo";
import PayBillModal from "./PayBillModal";
import { Bnet as BnetLogo } from "assests/images/bundles/paltel/paltelImage";
import {
  getBnetBillsApiCalled,
} from "shared/endPoints/apiCallers";
import translate from "i18n/translate";
import { useHistory } from "react-router-dom";
import EditableTextFieldForm from "./shared/InputBills";

const Bnet = () => {
  const [number, setNumber] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPills, setShowPills] = useState([]);
  const history = useHistory();

  useEffect(() => {
    const { state } = history.location;
    if (state?.number) {
      setNumber(state.number);
      fetchBnetBills({ number: state.number }).catch((error) =>
        console.error("Failed to prefetch Bnet bills:", error)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isLoading,
    mutateAsync: fetchBnetBills,
    data: bnetPills,
    reset,
  } = useMutation(getBnetBillsApiCalled, {
    onSuccess: async (res) => {
      if (res?.status === "failed") {
        setShowPills([]);
        setSelectedBill(null);
        setErrorMessage(translate("No bills found for this service number"));
        return;
      }

      setShowPills(res || []);
      setErrorMessage("");
    },
    onError: () => {
      setShowPills([]);
      setSelectedBill(null);
      setErrorMessage(translate("No bills found for this service number"));
    },
  });

  useEffect(() => {
    if (bnetPills?.length > 0) {
      setShowPills(bnetPills);
    }
  }, [bnetPills]);

  const onSubmit = async (value) => {
    setNumber(value);
    setSelectedBill(null);
    setShowPills([]);
    setErrorMessage("");

    try {
      await fetchBnetBills({ number: value });
    } catch (error) {
      console.error("Failed to fetch Bnet bills:", error);
    }
  };

  const clearSelections = () => {
    setSelectedBill(null);
    reset();
  };

  return (
    <div>
      <PageTitle title={translate("Bnet Bills")} backPage="/pay_bills">
        <BillLogo src={BnetLogo} alt="bnet" style={{ marginTop: 20 }} />
        <EditableTextFieldForm
          initialValue={number}
          label={translate("Bnet Service Number")}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />

        {showPills?.length > 0 && (
          <>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <p>
                {translate("Total Bills Amount")}:{""}
                <span className="text-info">
                  {selectedBill?.amount ?? 0} {translate("ILS")}
                </span>
              </p>
              <PayBillModal
                selectedBill={selectedBill}
                number={number}
                isDisabled={!selectedBill?.amount || isLoading}
                clearSelections={clearSelections}
                provider="bnet"
              />
            </div>
            <table className="table table-striped mt-4" id="bnet-bills-table">
              <thead id="bnet-bills-table-head">
                <tr>
                  <th>{translate("Ref")}</th>
                  <th>{translate("Date")}</th>
                  <th>{translate("Must Pay")}</th>
                  <th>{translate("Amount")}</th>
                  <th>{translate("Pay")}</th>
                </tr>
              </thead>
              <tbody id="bnet-bills-table-body">
                {bnetPills?.map((bill, i) => (
                  <tr key={i}>
                    <td>{bill.id || bill.ref}</td>
                    <td>{bill.date || bill.date1}</td>
                    <td>{bill.must_paid}</td>
                    <td>{bill.amount}</td>
                    <td>
                      <Checkbox
                        color="success"
                        sx={{
                          padding: 0,
                        }}
                        disabled={isLoading}
                        onChange={() =>
                          bill.amount === selectedBill?.amount
                            ? setSelectedBill(null)
                            : setSelectedBill(bill)
                        }
                        checked={i <= bnetPills.indexOf(selectedBill)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {bnetPills?.length === 0 && !isLoading && (
          <h5>{translate("No bills found for this service number")}</h5>
        )}
        {errorMessage && !isLoading && (
          <h5>{errorMessage}</h5>
        )}

        <Box sx={{ display: 'flex', marginTop: 4, marginBottom: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/pay_bills")}
            sx={{ minWidth: 120 }}
          >
            {translate("Back")}
          </Button>
        </Box>
      </PageTitle>
    </div>
  );
};

export default Bnet;
